import { fileFields, listFields, mimeTypes, permissionFields } from '@/shared/constants';
import type {
  BaseFile,
  DriveFile,
  DriveMimeType,
  DriveOrderBy,
  ExportMimeType,
  Permission,
  PermissionRole,
  Revision,
} from '@/shared/DriveTypes';
import { type UnionToIntersection } from '@/shared/types';
import dedent from 'dedent';
import { apiKey, baseURL, DEV, isBun, LEGACY, MB, pdfJSVersion, rootFolderQuery } from './constants';
import gup from './gup';
import { cacheImage, getImageCacheKey } from './imageCache';
import { log } from './logger';
import { checkRequestsDisabled } from './plugins/failRequests';
import { strip } from './strip';
import {
  getExportMimeType,
  getLegacyState,
  getMimeType,
  hasAPublicPermission,
  isADocument,
  isTokenFileScope,
  pick,
  reg,
  safeParse,
  sanitizeHTML,
  toQueryParams,
  toThrow,
} from './utils';
import { runWorker } from './workerUtils';

export type Token = {
  access_token: string;
  expires_at: number;
  expires_in: number;
  first_issued_at: number;
  id_token: string;
  idpId: 'google';
  login_hint: string;
  scope: string;
  session_state: {
    extraQueryParams: {
      authuser: string;
    };
  };
  token_type: 'Bearer';
};

// For the app to function we only needs these properties
// It is just simpler when setting and getting
export type PartialToken = {
  access_token: string;
  id_token?: string; // legacy uses id_token, new codebase can just directly get http cookies
  expires_in: number;
  scope: string;
};

export type Params = {
  create: Parameters<typeof create>[0];
  permissions: {
    create: Parameters<typeof permissions.create>[0];
  };
};

type DriveParams = {
  fields?: string;
  supportsAllDrives: true;
  includeItemsFromAllDrives: true;
};

export type GapiResult<T> = {
  // Use getPromise instead of the thenable interface the gapi.client library provides
  getPromise: () => Promise<T>;
};

type GapiResponse<T> = GapiResult<{ result: T }>;
type GapiBodyResponse<T> = GapiResult<{
  body: string;
  result: T;
}>;

export type Drive = {
  Create: {
    options: {
      path: 'https://www.googleapis.com/drive/v3/files';
      method: 'POST';
      body: {
        name: string;
        mimeType: DriveMimeType;
        parents?: [string];
      };
      params?: DriveParams;
    };
    response: GapiBodyResponse<DriveFile>;
  };
  //   CreateWithData: {
  //     options: {
  //       path: 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
  //       method: 'POST';
  //       body: FormData;
  //     };
  //     response: Promise<{ result: DriveFile }>;
  //   };
  Copy: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/copy`;
      method: 'POST';
      body: {
        name?: string;
        parents: [string];
      };
      params?: DriveParams;
    };
    response: GapiBodyResponse<DriveFile>;
  };
  Export: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/export`;
      params: DriveParams & {
        mimeType: ExportMimeType;
      };
    };
    response: GapiBodyResponse<{ body: string }>;
  };
  Get: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}`;
      params?: DriveParams & {
        alt?: 'media';
      };
    };
    response: GapiBodyResponse<DriveFile>;
  };
  // GetMedia: {
  //   options: {
  //     path: `https://www.googleapis.com/drive/v3/files/${string}`;
  //     params?: Params & {
  //       alt: 'media';
  //     };
  //   };
  //   response: GapiResponse<DriveFile>;
  // };
  List: {
    options: {
      path: 'https://www.googleapis.com/drive/v3/files';
      params: DriveParams & {
        q?: string;
        orderBy?: DriveOrderBy;
        pageSize?: number;
      };
    };
    response: GapiResponse<{ files: DriveFile[]; nextPageToken?: string }>;
  };
  Rename: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}`;
      method: 'PATCH';
      body: {
        trashed?: boolean;
        name?: string;
      };
      params?: DriveParams;
    };
    response: GapiResult<{ result: DriveFile }>;
  };
  Revisions: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/revisions`;
      method: 'GET';
      params: DriveParams & {
        pageSize?: number;
      };
    };
    response: GapiResult<{ result: { revisions: Revision[] } }>;
  };
  Trash: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}`;
      method: 'PATCH';
      body: {
        trashed: true;
      };
    };
    response: GapiResult<{ result: DriveFile }>;
  };
  Update: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}`;
      method: 'PATCH';
      params: DriveParams & {
        appProperties?: Record<string, string>;
        name?: string;
        mimeType?: DriveMimeType;
        addParents?: string; // A comma-separated list of parent IDs to add
        removeParents?: string; // A comma-separated list of parent IDs to remove
        trashed?: boolean;
        properties?: Record<string, string>;
      };
    };
    response: GapiResult<{ result: DriveFile }>;
  };
};

type Permissions = {
  Create: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/permissions`;
      method: 'POST';
      body: {
        type: 'user' | 'group' | 'domain' | 'anyone';
        role: PermissionRole;
        emailAddress?: string;
        domain?: string;
        allowFileDiscovery?: boolean;
      };
      params?: DriveParams & {
        sendNotificationEmail?: boolean;
      };
    };
    response: GapiResult<{ result: Permission }>;
  };
  Delete: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/permissions/${string}`;
      method: 'DELETE';
      params?: DriveParams;
    };
    response: GapiResult<{ result: Permission }>;
  };
  // Get: {
  //   options: {
  //     path: `https://www.googleapis.com/drive/v3/files/${string}/permissions/${string}`;
  //     params?: Params;
  //   };
  //   response: GapiResult<{ result: Permission }>;
  // };
  List: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/permissions`;
      method: 'GET';
      params?: DriveParams;
    };
    response: GapiResult<{ result: { permissions: Permission[] } }>;
  };
  // https://developers.google.com/drive/api/v3/reference/permissions/update
  Update: {
    options: {
      path: `https://www.googleapis.com/drive/v3/files/${string}/permissions/${string}`;
      method: 'PATCH';
      body: {
        role: PermissionRole;
      };
      params?: DriveParams & {
        transferOwnership?: boolean;
      };
    };
    response: GapiResult<{ result: Permission }>;
  };
};

type Docs = {
  // Get: {
  //   options: { path: `https://docs.googleapis.com/v1/documents/${string}` };
  //   response: Promise<{
  //     result: Document;
  //   }>;
  // };
  // BatchUpdate: {
  //   options: {
  //     path: `https://docs.googleapis.com/v1/documents/${string}:batchUpdate`;
  //     method: 'POST';
  //     body: {
  //       requests: {
  //         insertText: {
  //           text: string;
  //           location: {
  //             index: number;
  //           };
  //         };
  //       }[];
  //     };
  //   };
  //   response: Promise<{
  //     result: Document;
  //   }>;
  // };
  Create: {
    options: {
      path: 'https://docs.googleapis.com/v1/documents';
      method: 'POST';
      body: {
        title: string;
      };
    };
    response: GapiResult<{ result: Document }>;
  };
};

declare global {
  const gapi: {
    auth?: {
      getToken: () => PartialToken | undefined;
      setToken: (options: PartialToken) => void;
    };
    auth2?: {
      getAuthInstance: () => {
        currentUser: {
          get: () => {
            getBasicProfile: () => {
              getId: () => string;
              getEmail: () => string;
              getName: () => string;
              getImageUrl: () => string;
            };
            getAuthResponse: () => Token;
          };
          listen: (o: (o: { getAuthResponse: () => PartialToken }) => void) => void;
        };
        isSignedIn: {
          get: () => boolean;
          listen: (o: (o: boolean) => void) => void;
        };
        signIn: () => Promise<void>;
        signOut: () => Promise<void>;
      };
    };
    client: {
      getToken: () => PartialToken | null;
      request: {
        (options: Drive['Copy']['options']): Drive['Copy']['response'];
        (options: Drive['Create']['options']): Drive['Create']['response'];
        // (options: Drive['CreateWithData']['options']): Drive['CreateWithData']['response'];
        (options: Drive['Get']['options']): Drive['Get']['response'];
        (options: Drive['Rename']['options']): Drive['Rename']['response'];
        (options: Drive['Update']['options']): Drive['Update']['response'];
        (options: Drive['Trash']['options']): Drive['Trash']['response'];
        (options: Drive['List']['options']): Drive['List']['response'];
        (options: Drive['Export']['options']): Drive['Export']['response'];
        (options: Drive['Revisions']['options']): Drive['Revisions']['response'];

        // Permissions
        (options: Permissions['Create']['options']): Permissions['Create']['response'];
        (options: Permissions['Delete']['options']): Permissions['Delete']['response'];
        // (options: Permissions['Get']['options']): Permissions['Get']['response'];
        (options: Permissions['List']['options']): Permissions['List']['response'];
        (options: Permissions['Update']['options']): Permissions['Update']['response'];
        // Docs
        // (options: Docs['Get']['options']): Docs['Get']['response'];
        // (options: Docs['BatchUpdate']['options']): Docs['BatchUpdate']['response'];
        (options: Docs['Create']['options']): Docs['Create']['response'];
        // Drives
        // (options: Drives['Get']['options']): Drives['Get']['response'];
      };
      setApiKey: (s: string) => void;
      setToken: (options: PartialToken) => void;
    };
    load: (s: string, callback: () => void) => void;
  };
}

// If you try to fetch a public file while authenticated with a drive.file scope it will return a 404, so hit the api directly
const clientRequest = (args: { path: string; params?: Record<string, unknown> }) => {
  const params = toQueryParams({
    ...('fields' in args && typeof args.fields === 'string' && { fields: args.fields }),
    ...('params' in args && args.params ? args.params : {}),
    key: apiKey,
  });
  return {
    getPromise: async () => {
      const req = await fetch(`${args.path.replace('https://www', 'https://content')}${params}`);
      const text = await req.text();
      return { body: text };
    },
  };
};

// large public documents must be fetched via mobilebasic to avoid drive export size restrictions (10 MB limit)
const mobileBasicRequest = (args: { path: string }) => {
  return {
    getPromise: async () => {
      const id = args.path.split('/').at(-2);
      if (!id) throw new Error('Invalid path format for mobileBasicRequest');
      const req = await fetch(`https://docs.google.com/document/d/${id}/mobilebasic?rm=minimal`);
      if (!req.ok) throw new Error(`Failed to fetch document: ${req.status}`);
      const text = await req.text();
      const body = text
        .replace(/<head>[\s\S]*?<\/head>/i, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<div[^>]*class="doc-content"[^>]*>/, (match) => {
          const bgColor = match.match(/background-color:\s*([^;]+)/)?.[1];
          return bgColor ? `<div style="background-color:${bgColor}">` : '<div>';
        });
      return { body };
    },
  };
};

// @ts-expect-error - Complex overloaded function wrapper
const driveRequest: typeof gapi.client.request = (args) => {
  // Check all requests for driveId
  if (DEV) {
    checkRequestsDisabled();
    const path = 'path' in args ? args.path : '';
    const fields = 'params' in args && 'fields' in args.params ? args.params.fields : '';
    const alt = 'params' in args && 'alt' in args.params ? args.params.alt : '';
    const pathPrefix = path.split('/').slice(0, -1).join('/');
    if (
      !path.endsWith('/export') &&
      !path.includes('/permissions') &&
      !path.includes('/revisions') &&
      !(pathPrefix === 'https://www.googleapis.com/drive/v3/files' && alt === 'media') &&
      !fields.includes('driveId') &&
      !gup('fields') &&
      gapi.client.getToken()
    ) {
      log.error(`Request is missing field 'driveId': ${JSON.stringify(args, null, 2)}`);
    }
  }
  const token = gapi.client.getToken();
  const query = 'params' in args ? ('q' in args.params ? args.params.q : undefined) : undefined;

  if (isTokenFileScope(token) && !query?.startsWith(rootFolderQuery)) {
    const body = 'body' in args ? args.body : undefined;
    const params = toQueryParams({
      ...('fields' in args && typeof args.fields === 'string' && { fields: args.fields }),
      ...('params' in args && args.params ? args.params : {}),
    });
    const request = fetch(`${args.path.replace('https://www.googleapis.com', baseURL)}${params}`, {
      ...('method' in args && {
        method: args.method === 'PATCH' ? 'PUT' : args.method,
      }),
      ...(LEGACY
        ? {
            headers: getHeaders(token),
          }
        : {
            credentials: 'include',
          }),
      ...(body && { body: JSON.stringify(body) }),
    });
    return {
      getPromise: async () => {
        let res = await request;
        const url = res.url;
        if (!res.ok) {
          throw new Error(`Failed to fetch file: ${res.statusText}\n${JSON.stringify(args, null, 2)}`);
        }

        const searchParams = new URLSearchParams(url.split('?')[1]);
        const mimeType = searchParams.get('mimeType');
        const contentType = res.headers.get('Content-Type');
        if (contentType === mimeTypes.pdf) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          return { body: url };
        }
        if (mimeType === mimeTypes.spreadsheet || mimeType === mimeTypes.xlsx || contentType === mimeTypes.xlsx) {
          return { body: await res.arrayBuffer() };
        }
        const text = await res.text();
        const json = safeParse(text);
        return json
          ? typeof json === 'object' && 'result' in json
            ? json
            : { result: json }
          : { body: text, result: json || {} };
      },
    };
  }
  return gapi.client.request(args as Parameters<typeof gapi.client.request>[number]);
};

const getHeaders = (token = gapi.client.getToken()) => {
  const state = getLegacyState();

  return {
    ...(token && {
      token: JSON.stringify(pick(token, 'access_token', 'id_token')),
    }),
    ...(state && {
      wikiid: state.workspace ? state.workspace.id : state.workspaceId,
    }),
  };
};

export const permissions = {
  list: async ({
    id,
    fields = 'permissions(id,role,type,emailAddress,displayName,photoLink,deleted)',
  }: {
    id: string;
    fields?: string;
  }): Promise<Permission[]> => {
    log.info('permissions.list', id, fields);
    const response = await driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${id}/permissions`,
      method: 'GET',
      params: {
        fields,
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
      },
    }).getPromise();

    return response.result.permissions;
  },
  create: async ({
    fileId,
    role,
    type,
    emailAddress,
    domain,
    driveId,
    allowFileDiscovery,
    sendNotificationEmail = false,
    fields = permissionFields,
  }: {
    fileId: string;
    role: Permission['role'];
    type: Permission['type'];
    emailAddress?: string;
    domain?: string;
    driveId?: string | undefined;
    allowFileDiscovery?: boolean;
    sendNotificationEmail?: boolean;
    fields?: string;
  }) => {
    log.info('permissions.create', fileId, role, type, emailAddress);

    const { result } = await driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
      method: 'POST',
      body: {
        role,
        type,
        ...(emailAddress && { emailAddress }),
        ...(domain && { domain }),
        ...(allowFileDiscovery !== undefined && { allowFileDiscovery }),
      },
      params: {
        fields,
        sendNotificationEmail,
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
        ...(driveId && {
          corpora: 'drive',
          driveId,
        }),
      },
    }).getPromise();

    return result;
  },
  update: async ({
    fileId,
    permissionId,
    role,
    transferOwnership,
    driveId,
    fields = permissionFields,
  }: {
    fileId: string;
    permissionId: string;
    role: PermissionRole;
    transferOwnership?: boolean;
    driveId?: string | undefined;
    fields?: string;
  }): Promise<Permission> => {
    log.info('permissions.update', fileId, permissionId, role);
    const request = driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${fileId}/permissions/${permissionId}`,
      method: 'PATCH',
      body: {
        role,
      },
      params: {
        fields,
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
        ...(transferOwnership && { transferOwnership }),
        ...(driveId && {
          corpora: 'drive',
          driveId,
        }),
      },
    });

    const response = await request.getPromise();
    return response.result;
  },
  delete: async ({
    fileId,
    permissionId,
    driveId,
  }: {
    fileId: string;
    permissionId: string;
    driveId?: string | undefined;
  }) => {
    log.info('permissions.update', fileId, permissionId);

    const { result } = await driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${fileId}/permissions/${permissionId}`,
      method: 'DELETE',
      params: {
        ...(driveId && {
          corpora: 'drive',
          driveId,
        }),
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
      },
    }).getPromise();
    return result;
  },
};

export const get = async ({
  id,
  fields = fileFields,
  client = false,
}: {
  id: string;
  fields?: string;
  client?: boolean;
}) => {
  const method = client ? gapi.client.request : driveRequest;
  const response = await method({
    path: `https://www.googleapis.com/drive/v3/files/${id}`,
    params: {
      fields,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
    },
  }).getPromise();
  return response.result;
};

export const trash = async ({ id, useClient = false }: { id: string; useClient?: boolean }) => {
  const method = useClient ? gapi.client.request : driveRequest;
  const request = method({
    path: `https://www.googleapis.com/drive/v3/files/${id}`,
    method: 'PATCH',
    body: {
      trashed: true,
    },
    params: {
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
    },
  });

  const response = await request.getPromise();
  return response.result;
};

export const create = async ({
  name,
  mimeType,
  parents,
  shortcutDetails,
  properties,
  fields = fileFields,
}: {
  name: string;
  mimeType: DriveMimeType;
  parents: [string];
  shortcutDetails?: {
    targetId: string;
  };
  properties?: BaseFile['properties'];
  fields?: string;
}) => {
  if (DEV) checkRequestsDisabled();

  // drive.file permission allows this so use gapi.client
  const { result } = await gapi.client
    .request({
      path: 'https://www.googleapis.com/drive/v3/files',
      method: 'POST',
      body: {
        name,
        mimeType,
        parents,
        ...(shortcutDetails && { shortcutDetails }),
        ...(properties !== undefined && { properties }),
      },
      params: {
        fields,
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
      },
    })
    .getPromise();
  return result;
};

export const createDocument = async ({ title }: { title: string }): Promise<Document> => {
  if (DEV) checkRequestsDisabled();
  // document permission allows this so use gapi.client
  const { result } = await gapi.client
    .request({
      path: 'https://docs.googleapis.com/v1/documents',
      method: 'POST',
      body: {
        title,
      },
    })
    .getPromise();

  return result;
};

export const copy = async ({
  id,
  name,
  parents,
  fields = fileFields,
  useClient = false,
}: {
  id: string;
  name: string;
  parents: [string];
  fields?: string;
  useClient?: boolean;
}) => {
  const method = useClient ? gapi.client.request : driveRequest;
  const request = method({
    path: `https://www.googleapis.com/drive/v3/files/${id}/copy`,
    method: 'POST',
    body: {
      name,
      parents,
    },
    params: {
      fields,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
    },
  });
  const response = await request.getPromise();
  return response.result;
};

export const copyAll = async ({
  folder,
  files,
  fields = fileFields,
  useClient = false,
}: {
  folder: DriveFile;
  files: DriveFile[];
  fields?: string;
  useClient?: boolean;
}): Promise<DriveFile[]> => {
  const copiedFiles: DriveFile[] = [];
  const parentIdMap = new Map<string, string>();
  files.unshift(folder);

  for (const file of files) {
    const parentId = file.parents?.[0];
    if (!parentId) throw new Error(`File ${file.name} (${file.id}) is missing parent ID`);
    const parents: [string] =
      file === folder
        ? [parentId]
        : [
            parentIdMap.get(parentId) ??
              toThrow(`Parent ID ${parentId} for file ${file.name} (${file.id}) not found in map`),
          ];

    if (file.mimeType === mimeTypes.folder) {
      const result = await create({
        name: file === folder ? `Copy of ${file.name}` : file.name,
        mimeType: mimeTypes.folder,
        parents,
        fields,
      });
      parentIdMap.set(file.id, result.id);
      copiedFiles.push(result);
    } else {
      const result = await copy({
        id: file.id,
        name: file.name,
        parents,
        fields,
        useClient,
      });
      copiedFiles.push(result);
    }
  }

  return copiedFiles;
};

export const listRevisions = async ({
  fileId,
  fields = 'revisions(id,modifiedTime,lastModifyingUser,keepForever,published)',
  pageSize = 10,
}: {
  fileId: string;
  fields?: string;
  pageSize?: number;
}): Promise<Revision[]> => {
  const { result } = await driveRequest({
    path: `https://www.googleapis.com/drive/v3/files/${fileId}/revisions`,
    method: 'GET',
    params: {
      fields,
      pageSize,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
    },
  }).getPromise();

  return result.revisions;
};

export const rename = async ({ id, name, fields = fileFields }: { id: string; name: string; fields?: string }) => {
  const request = driveRequest({
    path: `https://www.googleapis.com/drive/v3/files/${id}`,
    method: 'PATCH',
    body: {
      name,
    },
    params: {
      fields,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
    },
  });
  const response = await request.getPromise();
  return response.result;
};

export const update = async ({
  id,
  name,
  mimeType,
  addParents,
  removeParents,
  trashed,
  properties,
  fields = fileFields,
  useClient = false,
}: {
  id: string;
  name?: string;
  mimeType?: DriveMimeType;
  addParents?: string;
  removeParents?: string;
  trashed?: boolean;
  properties?: BaseFile['properties'] | undefined;
  fields?: string;
  useClient?: boolean;
}) => {
  // console.log('update', id, name, mimeType, addParents, removeParents, trashed, properties, fields);
  const method = useClient ? gapi.client.request : driveRequest;
  const request = method({
    path: `https://www.googleapis.com/drive/v3/files/${id}`,
    method: 'PATCH',
    body: {
      ...(name && { name }),
      ...(mimeType && { mimeType }),
      ...(properties && { properties }),
      ...(trashed !== undefined && { trashed }),
    },
    params: {
      fields,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
      ...(addParents && { addParents }),
      ...(removeParents && { removeParents }),
    },
  });
  const response = await request.getPromise();
  return response.result;
};

export const search = async ({
  q,
  fields = listFields,
  pageSize = 1000,
  trashed = false,
  driveId,
  nextPageToken,
}: {
  q: string;
  fields?: string;
  pageSize?: number;
  trashed?: boolean;
  driveId?: string | undefined;
  nextPageToken?: string;
}): Promise<DriveFile[]> => {
  // note: needs 'name' field in 'file(fields)', otherwise empty array is always returned
  if (!fields.includes('name')) {
    throw new Error('fields must include "name"');
  }

  const { result } = await driveRequest({
    path: 'https://www.googleapis.com/drive/v3/files',
    params: {
      q: `${q} and trashed = ${trashed}`,
      fields: `${fields},nextPageToken`,
      pageSize,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
      ...(nextPageToken && { pageToken: nextPageToken }),
      ...(driveId && {
        corpora: 'drive',
        driveId,
      }),
    },
  }).getPromise();

  if (result.nextPageToken) {
    const files = await search({
      q,
      fields,
      pageSize,
      trashed,
      driveId,
      nextPageToken: result.nextPageToken,
    });
    return result.files.concat(files);
  }
  return result.files;
};

export async function* listStream({
  query,
  fields = listFields,
  orderBy = 'folder,name_natural',
  pageSize = 1000,
  trashed = false,
  driveId,
  nextPageToken,
}: {
  query: string;
  fields?: string;
  orderBy?: DriveOrderBy;
  pageSize?: number;
  trashed?: boolean;
  driveId?: string | undefined;
  nextPageToken?: string;
}): AsyncGenerator<DriveFile[], void, unknown> {
  const q = `${query} and trashed = ${trashed}`;
  log.info('listStream:', q);
  const { result } = await driveRequest({
    path: 'https://www.googleapis.com/drive/v3/files',
    params: {
      q,
      fields: `${fields},nextPageToken`,
      pageSize,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
      orderBy,
      ...(nextPageToken && { pageToken: nextPageToken }),
      ...(driveId && {
        corpora: 'drive',
        driveId,
      }),
    },
  }).getPromise();

  if (result.nextPageToken) {
    yield result.files;
    const stream = listStream({
      query,
      fields,
      orderBy,
      pageSize,
      trashed,
      driveId,
      nextPageToken: result.nextPageToken,
    });

    // It's possible for Drive to return a duplicate file in the next page as the first entry
    for await (const files of stream) {
      if (files.at(0)?.id === result.files.at(-1)?.id) {
        yield files.slice(1);
      } else {
        yield files;
      }
    }
    return;
  }
  yield result.files;
  return;
}

export const getLast = async ({ driveId }: { driveId?: string | undefined }): Promise<DriveFile | undefined> => {
  const { result } = await driveRequest({
    path: 'https://www.googleapis.com/drive/v3/files',
    params: {
      includeItemsFromAllDrives: true,
      supportsAllDrives: true,
      fields: listFields,
      pageSize: 1,
      ...(driveId && {
        driveId,
        corpora: 'drive',
      }),
    },
  }).getPromise();
  const [file] = result.files;
  return file;
};

export const list = async ({
  query,
  fields = listFields,
  orderBy = 'folder,name_natural',
  pageSize = 1000,
  trashed = false,
  driveId,
  nextPageToken,
}: {
  query: string;
  fields?: string;
  orderBy?: DriveOrderBy;
  pageSize?: number;
  trashed?: boolean;
  driveId?: string | undefined;
  nextPageToken?: string;
}): Promise<DriveFile[]> => {
  const q = `${query} and trashed = ${trashed}`;
  log.info('list:', q);

  const { result } = await driveRequest({
    path: 'https://www.googleapis.com/drive/v3/files',
    params: {
      q,
      fields: `${fields},nextPageToken`,
      pageSize,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
      orderBy,
      ...(nextPageToken && { pageToken: nextPageToken }),
      ...(driveId && {
        corpora: 'drive',
        driveId,
      }),
    },
  }).getPromise();
  if (!result) {
    log.error('error for list', q);
    // oxlint-disable-next-line no-console
    console.trace();
    return [];
  }

  if (result.nextPageToken) {
    const files = await list({
      query,
      fields,
      orderBy,
      pageSize,
      trashed,
      driveId,
      nextPageToken: result.nextPageToken,
    });

    // It's possible for Drive to return a duplicate file in the next page as the first entry
    if (files.at(0)?.id === result.files.at(-1)?.id) {
      return result.files.concat(files.slice(1));
    }
    return result.files.concat(files);
  }

  return result.files;
};

export const ancestor = async ({
  id,
  folderId,
  wikis,
  fields = 'id,name,parents',
}: {
  id: string;
  folderId: string;
  wikis: DriveFile[];
  fields?: string;
}): Promise<DriveFile> => {
  const file = await get({ id, fields });
  if (!file.parents) return file;
  if (wikis.some((wiki) => wiki.id === file.id)) return file;
  if (file.parents[0] === folderId) return file;
  return ancestor({ id: file.parents[0], folderId, wikis, fields });
};

export type ExportResult = UnionToIntersection<Awaited<ReturnType<typeof exportFile>>>;
export type DriveFileWithExport = DriveFile & { data: ExportResult };

export const exportFile = async ({
  id,
  fields,
  legacyHTML,
  file,
}: {
  id: string;
  fields?: string;
  legacyHTML?: string | undefined; // allows skipping double html export request on legacy
  file: DriveFile;
}) => {
  const mimeType = getMimeType(file);
  if (!mimeType) return;
  const isPublic = hasAPublicPermission(file);
  const exportType = getExportMimeType(mimeType);
  log.info('exportFile', id, mimeType, exportType);
  if (!exportType) {
    if (DEV) {
      // oxlint-disable-next-line no-console
      console.trace();
      log.error(`No exportType found for mimeType: ${mimeType}`);
    }
    return;
  }

  const token = gapi.client.getToken();
  if (mimeType === mimeTypes.docx) {
    const url = `https://docs.google.com/feeds/download/documents/export/Export?id=${id}`;
    const response = await fetch(
      url,
      token
        ? {
            headers: {
              Authorization: `Bearer ${token.access_token}`,
            },
          }
        : undefined
    );
    const html = await response.text();
    return { html };
  }

  if (mimeType === mimeTypes.ppt || mimeType === mimeTypes.pptx) {
    const url = `https://docs.google.com/feeds/download/presentations/Export?id=${id}&exportFormat=txt`;
    const response = await fetch(
      url,
      token
        ? {
            headers: {
              Authorization: `Bearer ${token.access_token}`,
            },
          }
        : undefined
    );
    const html = await response.text();
    return { html };
  }

  if (mimeType === mimeTypes.html) {
    const [response, sanitize] = await Promise.all([
      driveRequest({
        path: `https://www.googleapis.com/drive/v3/files/${id}`,
        params: {
          ...(fields && { fields }),
          alt: 'media',
          supportsAllDrives: true,
          includeItemsFromAllDrives: true,
        },
      }).getPromise(),
      import('dompurify').then((o) => o.default.sanitize),
    ]);
    return { html: sanitize(response.body) };
  }

  // PDF
  if (mimeType === mimeTypes.pdf) {
    // Start the pdf request async
    const request = driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${id}`,
      params: {
        ...(fields && { fields }),
        alt: 'media',
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
      },
    }).getPromise();

    // Start the worker download as soon as possible
    const [pdfjsLib, workerURL] = await Promise.all([
      import('pdfjs-dist'),
      // For now just use unpkg, so we have the same environment in dev and prod
      // Once legacy is removed, we can switch back to using the local build
      Promise.resolve(`https://unpkg.com/pdfjs-dist@${pdfJSVersion}/build/pdf.worker.min.mjs`),
    ]);
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerURL;

    const body = (await request).body;
    // Uses a blob URL for fetch and binary data for gapi.client
    // https://stackoverflow.com/a/75588333
    const data = body.startsWith('blob:') ? body : Uint8Array.from(body, (c) => c.charCodeAt(0)).buffer;
    const p = pdfjsLib.getDocument(data).promise;
    const pdfDocument = await p;
    const numPages = pdfDocument.numPages;
    const text = await Promise.all(
      Array.from({ length: numPages }, async (_, i) => {
        const page = await pdfDocument.getPage(i + 1);
        try {
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item) => ('str' in item ? item.str : '')).join(' ');
          return pageText;
        } catch (e) {
          log.error(e);
          return '';
        }
      })
    ).then((arr) => arr.join(' '));

    const url = body.startsWith('blob:')
      ? body
      : URL.createObjectURL(new Blob([new Uint8Array(await pdfDocument.getData())], { type: 'application/pdf' }));
    const page = await pdfDocument.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const firstPageHeight = page.getViewport({ scale: 802 / viewport.width }).height;
    const height = await Promise.all(
      Array.from({ length: numPages }, async (_, i) => {
        const page = await pdfDocument.getPage(i + 1);
        const { height } = page.getViewport({ scale: 805 / viewport.width });
        return height;
      })
    ).then((arr) => arr.reduce((acc, curr) => acc + curr, 0));

    // If there's are more than 30 pages, show 1 page
    // const totalHeight = numPages > 30 ? firstPageHeight : height + 2;
    const navBar = 58;
    const totalHeight = numPages > 30 ? firstPageHeight + navBar : height + 2;

    return {
      height: totalHeight,
      url,
      text,
    };
  }

  if (mimeType === mimeTypes.text || mimeType === mimeTypes.markdown || mimeType === mimeTypes.markdownX) {
    if (mimeType === mimeTypes.markdown || mimeType === mimeTypes.markdownX) {
      const [response, marked, sanitize] = await Promise.all([
        driveRequest({
          path: `https://www.googleapis.com/drive/v3/files/${id}`,
          params: {
            ...(fields && { fields }),
            alt: 'media',
            supportsAllDrives: true,
            includeItemsFromAllDrives: true,
          },
        }).getPromise(),
        import('marked'),
        import('dompurify').then((o) => o.default.sanitize),
      ]);
      const html = await marked.parse(response.body);
      return {
        html: sanitize(html),
      };
    }

    const response = await driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${id}`,
      params: {
        ...(fields && { fields }),
        alt: 'media',
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
      },
    }).getPromise();
    return {
      html: sanitizeHTML(response.body),
    };
  }

  // Document for modern codebase
  // uses markdown to get checkboxes and code block language
  // todo: should not call markdown for search
  if (isADocument(file)) {
    const size = Number(file.size);
    const method =
      isPublic && !isBun
        ? !Number.isNaN(size) && size > 10 * MB
          ? mobileBasicRequest
          : isTokenFileScope(token)
            ? clientRequest
            : gapi.client.request
        : driveRequest;

    const response = await Promise.allSettled([
      legacyHTML // Legacy provides it's own export
        ? Promise.resolve({ body: legacyHTML })
        : method({
            path: `https://www.googleapis.com/drive/v3/files/${id}/export`,
            params: {
              ...(fields && { fields }),
              mimeType: exportType as 'text/html',
              supportsAllDrives: true,
              includeItemsFromAllDrives: true,
            },
          }).getPromise(),
      isBun
        ? Promise.resolve({ body: '' })
        : method({
            path: `https://www.googleapis.com/drive/v3/files/${id}/export`,
            params: {
              ...(fields && { fields }),
              mimeType: 'text/markdown',
              supportsAllDrives: true,
              includeItemsFromAllDrives: true,
            },
          }).getPromise(),
    ]);

    const [htmlResponse, markdownResponse] = response;
    if (htmlResponse.status === 'rejected') {
      throw htmlResponse.reason;
    }
    if (markdownResponse.status === 'rejected') {
      throw markdownResponse.reason;
    }
    let html = htmlResponse.value.body;
    if (html) {
      const cachePromises: Promise<void>[] = [];
      const keys: string[] = [];
      for (const match of html.matchAll(reg.global.base64Image)) {
        const base64 = match[2] ?? '';
        const key = getImageCacheKey(base64);
        keys.push(key);
        cachePromises.push(cacheImage(key, base64));
      }
      await Promise.allSettled(cachePromises);
      let index = 0;
      html = html.replace(
        reg.global.base64Image,
        (_match, before: string | undefined, _base64: string, after: string | undefined) => {
          const beforeStyle = before?.match(/style="([^"]*)"/)?.[1] ?? '';
          const afterStyle = after?.match(/style="([^"]*)"/)?.[1] ?? '';
          const style = [beforeStyle, afterStyle].filter(Boolean).join(';');
          const key = keys[index++];
          return `<cache-img data-key="${key}"${style ? ` style="${style}"` : ''}></cache-img>`;
        }
      );
    }

    return {
      html,
      markdown: markdownResponse.value.body,
    };
  }

  // Sheets
  if (mimeType === mimeTypes.spreadsheet || mimeType === mimeTypes.xlsx) {
    const isXLSX = mimeType === mimeTypes.xlsx;
    const response = await driveRequest({
      path: `https://www.googleapis.com/drive/v3/files/${id}${isXLSX ? '' : '/export'}`,
      params: {
        ...(fields && { fields }),
        ...(isXLSX
          ? {
              alt: 'media',
            }
          : {
              mimeType: exportType,
            }),
        supportsAllDrives: true,
        includeItemsFromAllDrives: true,
      },
    }).getPromise();

    const buffer =
      typeof response.body === 'string' ? Uint8Array.from(response.body, (c) => c.charCodeAt(0)).buffer : response.body;

    const pathSheetIndex = window.location.search.match(/s=(\d+)/)?.[1];

    const worker = new Worker(new URL('./workers/excelWorker.ts', import.meta.url), { type: 'module' });
    const { worksheets, activeSheetIndex } = await runWorker<
      { type: 'parse'; buffer: ArrayBuffer; pathSheetIndex?: string | undefined },
      | { type: 'success'; worksheets: Array<{ name: string; html: string }>; activeSheetIndex: number }
      | { type: 'error'; error: string }
    >(
      worker,
      {
        type: 'parse',
        buffer,
        pathSheetIndex,
      },
      [buffer]
    );

    const output = worksheets.map((worksheet, i) => {
      const { html } = worksheet;

      if (html === '<table></table>') {
        return '';
      }

      const showTable =
        worksheets.length === 1
          ? ''
          : (pathSheetIndex && Number(pathSheetIndex) === i) || (!pathSheetIndex && i === 0)
            ? ''
            : ' class="hide"';

      if (worksheets.length > 1 && i === worksheets.length - 1) {
        const s = dedent /* js */ `
          function showSheet(e, i) {
            e.preventDefault();
            const sheets = document.querySelectorAll('[data-table]');
            sheets.forEach((sheet) => sheet.classList.add('hide'));
            sheets[i].classList.remove('hide');
            const links = document.querySelectorAll('[data-link]');
            links.forEach((link) => {
              link.style.fontWeight = '400';
              link.style.color = '#444746';
              link.style.background = '#fafbfd';
              link.style.borderTopColor = '#dadce0';
              link.style.borderLeftColor = '#dadce0';
              link.style.borderBottomColor = '#dadce0';
              link.style.zIndex = '0';
            });
            e.target.style.fontWeight = '500';
            e.target.style.color = '#1a73e8';
            e.target.style.background = '#e8f0fe';
            e.target.style.borderTopColor = '#dadce0';
            e.target.style.borderLeftColor = '#dadce0';
            e.target.style.borderBottomColor = 'transparent';
            e.target.style.zIndex = '1';
            if(i > 0){
              history.replaceState({}, '', '?s=' + i);
            } else if(window.location.search.includes('s=')){
              history.replaceState({}, '', window.location.pathname);
            }
          }
        `;

        const activeSheet = String(activeSheetIndex);
        const getLinkstyle = (active = false) => `
          font-size: 14px;
          text-decoration: none;
          padding: 8px 16px;
          color: ${active ? '#1a73e8' : '#5f6368'};
          display: inline-flex;
          align-items: center;
          border: none;
          background: ${active ? '#e8f0fe' : 'transparent'};
          font-weight: ${active ? '500' : '400'};
          border-radius: 4px 4px 0 0;
        `;

        const getLink = (wsName: string, i: number) => {
          const linkStyle = getLinkstyle(activeSheet === i.toString());
          return /*html*/ `<a title="${wsName}" data-link onclick="${s}; showSheet(event, ${i})" style="${linkStyle}" href="?s=${i}">${wsName}</a>`;
        };

        let linksHTML = '';
        for (let idx = 0; idx < worksheets.length; idx++) {
          const ws = worksheets[idx];
          if (ws) linksHTML += getLink(ws.name, idx);
        }
        return strip /*html*/ `
          <div style="overflow-x: auto" data-table${showTable}>${html}</div>
          <iframe data-iframe class="hide" style="width: 100%; height: calc(100vh - 500px); border: none;"></iframe>
          <div style="display:inline-flex; align-items: flex-end; color: #444746; margin-top: .75rem;">
            <div class="flex flex-ac flex-jc" style="width: 45px; margin-right: 0px" data-react="SheetSettings"></div>
            <div data-links class="flex" style="align-items: flex-end;">${linksHTML}</div>
          </div>
        `;
      }

      if (worksheets.length === 1) {
        return strip /* html */ `
          <div style="overflow-x: auto" data-table${showTable}>${html}</div>
          <iframe data-iframe class="hide" style="width: 100%; height: calc(100vh - 500px); border: none;"></iframe>
          <div class="flex flex-ac flex-jc" style="width: 45px; margin-top: .25rem;" data-react="SheetSettings"></div>
      `;
      }

      return strip /* html */ `
        <div style="overflow-x: auto" data-table${showTable}>${html}</div>
      `;
    });

    const html = output.length === 1 ? (output[0] as string) : output.join('');
    return {
      html,
    };
  }

  // Everything else
  const response = await driveRequest({
    path: `https://www.googleapis.com/drive/v3/files/${id}/export`,
    params: {
      ...(fields && { fields }),
      mimeType: exportType,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true,
    },
  }).getPromise();

  return {
    html: response.body,
  };

  // if (isExportable({ mimeType } as DriveFile)) {}

  // return {
  //   html: '',
  // };
};
